/** Payment endpoints (`/payments`). Management records offline; clients pay via Stripe. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, postData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import {
  ClientLedger,
  Payment,
  PaymentMethod,
  PaymentReceipt,
  ReceivablesSummary,
} from "../types/models";

export interface ListPaymentsParams {
  page?: number;
  limit?: number;
  clientId?: string;
  method?: PaymentMethod;
  dateFrom?: string;
  dateTo?: string;
}

/** Offline methods recorded by staff — card payments go through Stripe. */
export type OfflinePaymentMethod = Exclude<PaymentMethod, "CARD">;

export interface RecordPaymentInput {
  clientId: string;
  amount: number;
  method: OfflinePaymentMethod;
  orderId?: string;
  reference?: string;
  note?: string;
}

export interface StripeIntentInput {
  amount: number;
  orderId?: string;
}

export const paymentsApi = {
  list: (params?: ListPaymentsParams) => getList<Payment>("/payments", params),
  record: (input: RecordPaymentInput) => postData<Payment>("/payments", input),
  receivables: () => getData<ReceivablesSummary>("/payments/receivables"),
  clientLedger: (id: string) =>
    getData<ClientLedger>(`/payments/clients/${id}/ledger`),
  myLedger: () => getData<ClientLedger>("/payments/ledger/me"),
  receipt: (id: string) => getData<PaymentReceipt>(`/payments/${id}/receipt`),
  createStripeIntent: (input: StripeIntentInput) =>
    postData<{ clientSecret: string | null; paymentIntentId: string }>(
      "/payments/stripe/intent",
      input,
    ),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const usePayments = (params?: ListPaymentsParams) =>
  useQuery({
    queryKey: queryKeys.payments.list(params),
    queryFn: () => paymentsApi.list(params),
    placeholderData: (prev) => prev,
  });

export const useReceivables = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.payments.receivables,
    queryFn: () => paymentsApi.receivables(),
    enabled,
  });

export const useClientLedger = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.payments.clientLedger(id ?? ""),
    queryFn: () => paymentsApi.clientLedger(id as string),
    enabled: Boolean(id),
  });

export const useMyLedger = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.payments.myLedger,
    queryFn: () => paymentsApi.myLedger(),
    enabled,
  });

export const useRecordPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordPaymentInput) => paymentsApi.record(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payments.all });
      qc.invalidateQueries({ queryKey: queryKeys.clients.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.overview });
    },
  });
};

export const useCreateStripeIntent = () =>
  useMutation({
    mutationFn: (input: StripeIntentInput) =>
      paymentsApi.createStripeIntent(input),
  });
